import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import useBracketStore from '@/stores/useBracketStore'
import useBracketScroll from '@/hooks/useBracketScroll'
import { linkHorizontalScroll } from '@/lib/scrollSync'
import { BracketActualWinnerCell, BracketConnectorLines, BracketMatchCard, BracketPickCell, RoundCarousel, RoundLockBadge } from './index'

// Layout constants
const EXTRA_HEIGHT_PADDING = 100
const CARD_WIDTH = 280
const ROUND_WIDTH = 320

export default function BracketRoundRenderer() {
  // ── Store selectors ──
  const bracketStructure = useBracketStore(s => s.bracketStructure)
  const bracket = useBracketStore(s => s.bracket)
  const matches = useBracketStore(s => s.matches)
  const picks = useBracketStore(s => s.picks)
  const viewMode = useBracketStore(s => s.viewMode)
  const isMobile = useBracketStore(s => s.isMobile)
  const liveStats = useBracketStore(s => s.liveStats)
  const canEditRound = useBracketStore(s => s.canEditRound)
  const getRoundLockInfo = useBracketStore(s => s.getRoundLockInfo)
  const isMatchupsPending = useBracketStore(s => s.isMatchupsPending)
  const isViewingOfficial = useBracketStore(s => s.isViewingOfficial)
  const isEditingOfficial = useBracketStore(s => s.isEditingOfficial)
  const viewerHiddenRounds = useBracketStore(s => s.viewerHiddenRounds) || []
  const bracketRoundCount = useBracketStore(s => s.bracketRoundCount)
  const mobileRoundIndex = useBracketStore(s => s.mobileRoundIndex)
  const displayRoundIndex = useBracketStore(s => s.displayRoundIndex)
  const setMobileRoundIndex = useBracketStore(s => s.setMobileRoundIndex)
  const setDisplayRoundIndex = useBracketStore(s => s.setDisplayRoundIndex)
  const getTeamInfo = useBracketStore(s => s.getTeamInfo)
  const getMatchTeams = useBracketStore(s => s.getMatchTeams)
  const getPointsForRound = useBracketStore(s => s.getPointsForRound)
  const getRoundLabel = useBracketStore(s => s.getRoundLabel)
  const isTeamEliminatedInPreviousRounds = useBracketStore(s => s.isTeamEliminatedInPreviousRounds)
  const handleSelectWinner = useBracketStore(s => s.handleSelectWinner)

  // ── Scroll hook (manages refs) ──
  const scroll = useBracketScroll({
    isMobile,
    bracketRoundCount,
    bracketStructure,
    matches,
    viewMode,
  })

  const teamSize = bracket?.divisionTeamSize || 1
  const CELL_H = 40 + Math.max(0, teamSize - 1) * 24
  const MATCH_HEIGHT = 38 + 2 * CELL_H
  const MATCH_HEIGHT_3CELL = 38 + 3 * CELL_H
  const MATCH_HEIGHT_4CELL = 38 + 4 * CELL_H
  const MATCH_SPACING = 40
  const isCompact = viewMode === 'compact'
  const hasPQ = bracketStructure.some(r => r.roundNum === 0)
  const round1Matches = bracketStructure.find(r => r.roundNum === 1)?.matches || []
  const maxRound1MatchNumber = round1Matches.length > 0 ? Math.max(...round1Matches.map(m => m.match_number)) : 1
  const baseStep = MATCH_HEIGHT + MATCH_SPACING
  const round1Step = hasPQ ? baseStep * Math.pow(2, 1) : baseStep
  const round1TopOffset = hasPQ ? (baseStep * (Math.pow(2, 1) - 1)) / 2 : 0

  const getRoundLayout = (roundNum, roundMatches) => {
    const maxMatchNumber = Math.max(...roundMatches.map(m => m.match_number))
    const effectiveRound = hasPQ && roundNum >= 1 ? roundNum + 1 : roundNum
    const roundStep = isCompact
      ? baseStep
      : (roundNum === 0 ? baseStep : baseStep * Math.pow(2, effectiveRound - 1))
    const roundTopOffset_ = isCompact
      ? 0
      : (roundNum === 0 ? 0 : (baseStep * (Math.pow(2, effectiveRound - 1) - 1)) / 2)
    let pqTotalHeight = round1TopOffset + (maxRound1MatchNumber - 1) * round1Step + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING
    if (roundNum === 0 && roundMatches.length > 0) {
      let maxPQPosition = 0
      roundMatches.forEach(match => {
        const feedsIntoMatchNum = match.feeds_into_match_number || match.match_number
        const feedsIntoSlot = match.feeds_into_slot || 1
        const round1MatchPosition = round1TopOffset + (feedsIntoMatchNum - 1) * round1Step
        const slotOffset = feedsIntoSlot === 1 ? 0 : baseStep
        const pqPosition = round1MatchPosition + slotOffset
        maxPQPosition = Math.max(maxPQPosition, pqPosition)
      })
      pqTotalHeight = Math.max(pqTotalHeight, maxPQPosition + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING)
    }
    const totalHeight = isCompact
      ? (maxMatchNumber - 1) * baseStep + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING
      : (roundNum === 0 ? pqTotalHeight : roundTopOffset_ + (maxMatchNumber - 1) * roundStep + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING)
    return { roundStep, roundTopOffset: roundTopOffset_, totalHeight, maxMatchNumber }
  }

  const renderMatchCards = ({ roundNum, matches: roundMatches }) => {
    const { roundStep, roundTopOffset: roundTopOffset_ } = getRoundLayout(roundNum, roundMatches)
    // Multi-week brackets lock round by round, so editability is a property of
    // THIS round, not the bracket. Legacy brackets get canEditPicks unchanged.
    const canEditThisRound = canEditRound(roundNum)
    // Viewing another player's entry: this round's picks are masked until it
    // locks and closes. Render an explicit "Hidden until picks close" state so
    // a masked round doesn't read as "made no picks" (ticket 5dacdb9f).
    const roundHiddenForViewer = viewerHiddenRounds.includes(roundNum)

    return roundMatches.map((match) => {
      let topPosition = 0

      if (isCompact) {
        topPosition = 0
      } else if (roundNum === 0) {
        const feedsIntoMatchNum = match.feeds_into_match_number || match.match_number
        const feedsIntoSlot = match.feeds_into_slot || 1
        const round1MatchPosition = round1TopOffset + (feedsIntoMatchNum - 1) * round1Step
        const slotOffset = feedsIntoSlot === 1 ? 0 : baseStep
        topPosition = round1MatchPosition + slotOffset
      } else {
        topPosition = roundTopOffset_ + (match.match_number - 1) * roundStep
      }

      // Get actual winners from previous round (for cells 2 & 3)
      const { team1Id: actualTeam1Id, team2Id: actualTeam2Id } = getMatchTeams(match)

      // Get user's ORIGINAL picks from previous round (for cells 1 & 4)
      // For the base round, use actual bracket teams. For later rounds, use user's picks.
      // If user has no pick for a previous round, show null (not the official winner).
      let userPickedTeam1Id = actualTeam1Id
      let userPickedTeam2Id = actualTeam2Id
      let team1FromIncorrectPick = false
      let team2FromIncorrectPick = false

      // When a round's matchups are AUTHORED by the league rather than implied
      // by bracket position (MLP playoffs: the bye teams choose their
      // quarterfinal opponent), there is no previous-round pick to carry
      // forward — everyone faces the real field. getMatchTeams already returns
      // the authored teams; skip the predicted-advancer overlay entirely.
      const matchupsAuthored = !!getRoundLockInfo(match.round)?.matchupsPublished
      // An authored round whose matchups aren't published yet shows TBD in both
      // slots — don't carry the previous round's winners forward (client DUB
      // report: quarterfinal winners must not auto-fill the semifinals).
      const matchupsTbd = isMatchupsPending(match.round)

      if (match.round > 0 && !matchupsAuthored && !matchupsTbd) {
        let prevMatch1 = null
        if (match.round === 1) {
          prevMatch1 = matches.find(m =>
            m.round === 0 &&
            m.feeds_into_round === match.round &&
            m.feeds_into_match_number === match.match_number &&
            m.feeds_into_slot === 1
          )
        } else {
          const prevRoundData = bracketStructure.find(r => r.roundNum === match.round - 1)
          if (prevRoundData) {
            const match1Index = (match.match_number * 2) - 2
            prevMatch1 = prevRoundData.matches[match1Index]
          }
        }

        if (prevMatch1) {
          const prevPick1 = picks[prevMatch1.match_id]
          if (prevPick1?.teamId) {
            userPickedTeam1Id = prevPick1.teamId
            if (prevPick1.winner_team_id && prevPick1.teamId !== prevPick1.winner_team_id) {
              team1FromIncorrectPick = true
            }
          } else if (prevPick1 && !prevPick1.teamId && !isViewingOfficial) {
            // User has no pick for this previous match — don't show official winner as their pick
            userPickedTeam1Id = null
          }
        }

        let prevMatch2 = null
        if (match.round === 1) {
          prevMatch2 = matches.find(m =>
            m.round === 0 &&
            m.feeds_into_round === match.round &&
            m.feeds_into_match_number === match.match_number &&
            m.feeds_into_slot === 2
          )
        } else {
          const prevRoundData = bracketStructure.find(r => r.roundNum === match.round - 1)
          if (prevRoundData) {
            const match2Index = (match.match_number * 2) - 1
            prevMatch2 = prevRoundData.matches[match2Index]
          }
        }

        if (prevMatch2) {
          const prevPick2 = picks[prevMatch2.match_id]
          if (prevPick2?.teamId) {
            userPickedTeam2Id = prevPick2.teamId
            if (prevPick2.winner_team_id && prevPick2.teamId !== prevPick2.winner_team_id) {
              team2FromIncorrectPick = true
            }
          } else if (prevPick2 && !prevPick2.teamId && !isViewingOfficial) {
            // User has no pick for this previous match — don't show official winner as their pick
            userPickedTeam2Id = null
          }
        }
      }

      // Official view: no incorrect pick indicators
      if (isViewingOfficial) {
        team1FromIncorrectPick = false
        team2FromIncorrectPick = false
      }

      // Hidden round: drop every team the card would show — even published
      // matchup pairings — so the two pick cells render the lock state and
      // nothing hints at the hidden picks.
      if (roundHiddenForViewer) {
        userPickedTeam1Id = null
        userPickedTeam2Id = null
        team1FromIncorrectPick = false
        team2FromIncorrectPick = false
      }

      // Display teams. For a matchups-pending round both slots are empty, so
      // render an explicit TBD placeholder rather than a blank cell.
      const tbdPlaceholder = matchupsTbd ? { players: [] } : null
      const team1 = userPickedTeam1Id ? getTeamInfo(userPickedTeam1Id) : tbdPlaceholder
      const team2 = userPickedTeam2Id ? getTeamInfo(userPickedTeam2Id) : tbdPlaceholder

      // Determine how many cells are visible
      const cell2Visible = !roundHiddenForViewer && actualTeam1Id && actualTeam1Id !== userPickedTeam1Id
      const cell3Visible = !roundHiddenForViewer && actualTeam2Id && actualTeam2Id !== userPickedTeam2Id
      // Cells 1 & 4 are the viewer's OWN pick for each slot. When the viewer made
      // no pick for this match, that slot's team is null and the outer cell would
      // render as an empty row. Only keep it as a placeholder when there's no
      // actual-winner cell (2 / 3) already standing in for that slot — otherwise
      // it's redundant blank space bracketing the real teams (client report:
      // "extra space after the second team"). Round 1 is unaffected: there
      // userPickedTeam*Id defaults to the actual team, so the outer cells are
      // never empty.
      const cell1Visible = userPickedTeam1Id != null || !cell2Visible
      const cell4Visible = userPickedTeam2Id != null || !cell3Visible
      const visibleCellCount = (cell1Visible ? 1 : 0) + (cell2Visible ? 1 : 0)
        + (cell3Visible ? 1 : 0) + (cell4Visible ? 1 : 0)

      const cardMinHeight = visibleCellCount >= 4 ? MATCH_HEIGHT_4CELL
        : visibleCellCount === 3 ? MATCH_HEIGHT_3CELL
        : MATCH_HEIGHT

      // Current match pick status
      const currentPick = picks[match.match_id]
      const isTeam1Selected = currentPick?.teamId === userPickedTeam1Id && userPickedTeam1Id != null
      const isTeam2Selected = currentPick?.teamId === userPickedTeam2Id && userPickedTeam2Id != null

      // "Has a pick" for points purposes means the pick names one of the two
      // teams this card actually shows. A saved pick that names neither -- a
      // TBD round, or a round whose authored matchups replaced the team rows
      // after the pick was saved -- renders with nothing highlighted, so a
      // pending-points badge on it reads as points for a matchup the user
      // never picked (DUB-292).
      const hasVisiblePick = isTeam1Selected || isTeam2Selected

      const hasOfficialWinner = isViewingOfficial ? false : !!currentPick?.winner_team_id
      const isPickIncorrect = isViewingOfficial ? false : hasOfficialWinner && currentPick?.teamId && currentPick?.teamId !== currentPick?.winner_team_id
      const isPickCorrect = isViewingOfficial ? false : hasOfficialWinner && currentPick?.teamId && currentPick?.teamId === currentPick?.winner_team_id

      const team1EliminatedEarlier = isViewingOfficial ? false : userPickedTeam1Id ? isTeamEliminatedInPreviousRounds(userPickedTeam1Id, match) : false
      const team2EliminatedEarlier = isViewingOfficial ? false : userPickedTeam2Id ? isTeamEliminatedInPreviousRounds(userPickedTeam2Id, match) : false

      const selectedTeamEliminated = isViewingOfficial ? false :
        (isTeam1Selected && (isPickIncorrect || team1FromIncorrectPick || team1EliminatedEarlier)) ||
        (isTeam2Selected && (isPickIncorrect || team2FromIncorrectPick || team2EliminatedEarlier))

      let isTeam1Winner = false
      let isTeam2Winner = false

      const isTeam1LoserNotPicked = isViewingOfficial ? false : userPickedTeam1Id && !isTeam1Selected && !team1FromIncorrectPick && !team1EliminatedEarlier && hasOfficialWinner && currentPick?.winner_team_id !== userPickedTeam1Id
      const isTeam2LoserNotPicked = isViewingOfficial ? false : userPickedTeam2Id && !isTeam2Selected && !team2FromIncorrectPick && !team2EliminatedEarlier && hasOfficialWinner && currentPick?.winner_team_id !== userPickedTeam2Id

      if (isViewingOfficial && currentPick?.teamId) {
        isTeam1Winner = isTeam1Selected
        isTeam2Winner = isTeam2Selected
      } else {
        isTeam1Winner = isTeam1Selected && isPickCorrect
        isTeam2Winner = isTeam2Selected && isPickCorrect
      }

      // DUB-40: the championship final has exactly one match in its round.
      // When it has an official winner, that team is the tournament champion
      // and should get the Crown/Trophy on whichever cell holds them — even
      // when the user picked wrong.
      const isFinalMatch = roundMatches.length === 1 && !isViewingOfficial
      const officialWinnerId = currentPick?.winner_team_id
      const championCellTeamId1 = isFinalMatch && hasOfficialWinner ? officialWinnerId : null
      const team1CellIsOfficialChampion = !!championCellTeamId1 && userPickedTeam1Id === championCellTeamId1
      const team2CellIsOfficialChampion = !!championCellTeamId1 && userPickedTeam2Id === championCellTeamId1
      const actualTeam1IsOfficialChampion = !!championCellTeamId1 && actualTeam1Id === championCellTeamId1
      const actualTeam2IsOfficialChampion = !!championCellTeamId1 && actualTeam2Id === championCellTeamId1

      return (
        <BracketMatchCard
          key={match.match_id}
          ref={(el) => {
            if (el) {
              scroll.matchRefs.current.set(match.match_id, el)
            } else {
              scroll.matchRefs.current.delete(match.match_id)
            }
          }}
          matchId={match.match_id}
          roundNum={roundNum}
          matchNumber={match.match_number}
          isPickCorrect={isPickCorrect}
          isPickIncorrect={isPickIncorrect}
          isChampionMatch={isPickCorrect && (liveStats.championTeamId === userPickedTeam1Id || liveStats.championTeamId === userPickedTeam2Id)}
          selectedTeamEliminated={selectedTeamEliminated}
          hasOfficialWinner={hasOfficialWinner}
          hasPick={hasVisiblePick}
          points={getPointsForRound(roundNum)}
          cardMinHeight={cardMinHeight}
          topPosition={topPosition}
          cardWidth={CARD_WIDTH}
          isCompact={isCompact}
          isMobile={isMobile}
        >
          {/* Cell 1 (Outer Top) */}
          {cell1Visible && (
          <BracketPickCell
            team={team1}
            teamSize={bracket?.divisionTeamSize || 1}
            teamId={userPickedTeam1Id}
            isSelected={isTeam1Selected}
            isPickCorrect={isPickCorrect}
            isPickIncorrect={isPickIncorrect}
            fromIncorrectPick={team1FromIncorrectPick}
            eliminatedEarlier={team1EliminatedEarlier}
            isLoserNotPicked={isTeam1LoserNotPicked}
            isChampionCorrect={liveStats.championWon && liveStats.championTeamId === userPickedTeam1Id}
            isWinner={isTeam1Winner}
            hasOfficialWinner={hasOfficialWinner}
            officialWinnerId={currentPick?.winner_team_id}
            championTeamId={liveStats.championTeamId}
            showBorderB={!cell2Visible}
            hiddenPick={roundHiddenForViewer}
            canEditPicks={!roundHiddenForViewer && canEditThisRound}
            // Auto-scroll to the next unfilled match after each pick.
            // findNextAvailableMatch walks within the current round first
            // (skip-aware) and only crosses into the next round once every
            // match in the current one is filled. Same behavior on player
            // picks and admin official-results entry.
            onClick={() => userPickedTeam1Id && canEditThisRound && handleSelectWinner(match, userPickedTeam1Id, scroll.scrollToMatch)}
            actualWinnerShown={cell2Visible || cell3Visible}
            isViewingOfficial={isViewingOfficial}
            isOfficialChampion={team1CellIsOfficialChampion}
          />
          )}

          {/* Cell 2 (Inner Top) */}
          {cell2Visible && (
            <BracketActualWinnerCell
              team={getTeamInfo(actualTeam1Id)}
              teamSize={bracket?.divisionTeamSize || 1}
              isWinner={!isViewingOfficial && currentPick?.winner_team_id === actualTeam1Id}
              isChampionCorrect={liveStats.championWon && liveStats.championTeamId === actualTeam1Id}
              championTeamId={liveStats.championTeamId}
              showBorderB
              isOfficialChampion={actualTeam1IsOfficialChampion}
              isUserChampionPick={!!liveStats.championTeamId && liveStats.championTeamId === actualTeam1Id}
            />
          )}

          {/* Cell 3 (Inner Bottom) */}
          {cell3Visible && (
            <BracketActualWinnerCell
              team={getTeamInfo(actualTeam2Id)}
              teamSize={bracket?.divisionTeamSize || 1}
              isWinner={!isViewingOfficial && currentPick?.winner_team_id === actualTeam2Id}
              isChampionCorrect={liveStats.championWon && liveStats.championTeamId === actualTeam2Id}
              championTeamId={liveStats.championTeamId}
              isOfficialChampion={actualTeam2IsOfficialChampion}
              isUserChampionPick={!!liveStats.championTeamId && liveStats.championTeamId === actualTeam2Id}
            />
          )}

          {/* Cell 4 (Outer Bottom) */}
          {cell4Visible && (
          <BracketPickCell
            team={team2}
            teamSize={bracket?.divisionTeamSize || 1}
            teamId={userPickedTeam2Id}
            isSelected={isTeam2Selected}
            isPickCorrect={isPickCorrect}
            isPickIncorrect={isPickIncorrect}
            fromIncorrectPick={team2FromIncorrectPick}
            eliminatedEarlier={team2EliminatedEarlier}
            isLoserNotPicked={isTeam2LoserNotPicked}
            isChampionCorrect={liveStats.championWon && liveStats.championTeamId === userPickedTeam2Id}
            isWinner={isTeam2Winner}
            hasOfficialWinner={hasOfficialWinner}
            officialWinnerId={currentPick?.winner_team_id}
            championTeamId={liveStats.championTeamId}
            showBorderB={false}
            hiddenPick={roundHiddenForViewer}
            canEditPicks={!roundHiddenForViewer && canEditThisRound}
            // See note on the team1 onClick above.
            onClick={() => userPickedTeam2Id && canEditThisRound && handleSelectWinner(match, userPickedTeam2Id, scroll.scrollToMatch)}
            actualWinnerShown={cell2Visible || cell3Visible}
            isViewingOfficial={isViewingOfficial}
            isOfficialChampion={team2CellIsOfficialChampion}
          />
          )}
        </BracketMatchCard>
      )
    })
  }

  // Mobile view
  if (isMobile) {
    const displayRound = bracketStructure[displayRoundIndex] || bracketStructure[mobileRoundIndex]
    const currentLabel = displayRound ? getRoundLabel(displayRound.roundNum, displayRound.matches.length) : ''
    return (
      <div style={{ width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', paddingLeft: '16px', paddingRight: '16px' }}>
        <div
          ref={scroll.bracketScrollRef}
          data-bracket-scrollport
          className="overflow-x-hidden"
          style={{ touchAction: 'auto', overflowX: 'hidden', overflowY: 'clip' }}
        >
          <div ref={scroll.bracketInnerRef} className="relative pl-1 py-2 w-full">
            <div className="relative">
              {bracketStructure.length > 1 && (
                <div className="flex items-center justify-between mb-4 sticky top-0 z-10 py-2 sm:hidden">
                  <button
                    type="button"
                    onClick={() => scroll.goToRound(useBracketStore.getState().mobileRoundIndex - 1)}
                    disabled={mobileRoundIndex <= 0}
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground disabled:opacity-30 transition-opacity active:scale-95"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Previous round</span>
                  </button>
                  <div className="flex flex-col items-center gap-1.5">
                    <h3 className="text-sm font-bold text-foreground transition-opacity duration-150">{currentLabel}</h3>
                    {displayRound && (
                      <RoundLockBadge
                        lockInfo={getRoundLockInfo(displayRound.roundNum)}
                        isEditingOfficial={isEditingOfficial}
                      />
                    )}
                    <div className="flex items-center gap-1">
                      {bracketStructure.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => scroll.goToRound(i)}
                          className={`rounded-full transition-all duration-200 ${
                            i === displayRoundIndex
                              ? 'w-4 h-1.5 bg-[var(--color-purple)]'
                              : 'w-1.5 h-1.5 bg-[var(--mauve-7)] active:bg-[var(--mauve-9)]'
                          }`}
                          aria-label={`Go to round ${i + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => scroll.goToRound(useBracketStore.getState().mobileRoundIndex + 1)}
                    disabled={mobileRoundIndex >= bracketStructure.length - 1}
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground disabled:opacity-30 transition-opacity active:scale-95"
                  >
                    <ChevronRight className="h-4 w-4" />
                    <span className="sr-only">Next round</span>
                  </button>
                </div>
              )}
              <RoundCarousel
                isMobile
                roundIndex={mobileRoundIndex}
                totalRounds={bracketStructure.length}
                onGoToRound={setMobileRoundIndex}
                onRoundHint={setDisplayRoundIndex}
                allRounds={bracketStructure}
                renderRound={renderMatchCards}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <DesktopBracketView
    scroll={scroll}
    bracketStructure={bracketStructure}
    getRoundLabel={getRoundLabel}
    getRoundLayout={getRoundLayout}
    isCompact={isCompact}
    renderMatchCards={renderMatchCards}
    getRoundLockInfo={getRoundLockInfo}
    isEditingOfficial={isEditingOfficial}
  />
}

/**
 * Desktop bracket renderer. The round labels live in a sibling sticky row
 * *outside* the horizontal/vertical scroll container so their sticky offset
 * binds to the page viewport (the scroll container is a scrollport for both
 * axes, which would otherwise trap sticky inside it). Horizontal scroll
 * between the labels row and the matches row is kept in sync via an effect.
 */
function DesktopBracketView({ scroll, bracketStructure, getRoundLabel, getRoundLayout, isCompact, renderMatchCards, getRoundLockInfo, isEditingOfficial }) {
  const labelsScrollRef = useRef(null)

  useEffect(() => {
    const matches = scroll.bracketScrollRef?.current
    const labels = labelsScrollRef.current
    if (!matches || !labels) return

    // Echo-aware link (see lib/scrollSync.js). The old rAF-flag version wrote
    // a stale scrollLeft back into the matches scrollport, which aborted the
    // post-pick smooth scroll to the next round column (ticket 74a54965).
    return linkHorizontalScroll(matches, labels)
  }, [scroll.bracketScrollRef, bracketStructure.length])

  // DUB-78 / DUB-84 / DUB-85 / DUB-86 (desktop) and DUB-103 / DUB-104 /
  // DUB-105 (mobile): the displayRoundIndex auto-scroll has been removed.
  // Previously, when the round changed (notification jump, mobile round-arrow
  // tap, etc.), this effect would recenter the first match of the target
  // round. The geometry was unreliable across desktop staggered layout and
  // mobile carousel — landing halfway between brackets, jittering the page
  // up-then-down on result entry, and on mobile shoving the page-level
  // horizontal scroll mid-carousel-animation so cards landed flush right.
  // Until the round-transition geometry is reworked, the page no longer
  // auto-scrolls after picks or round changes; the user controls scroll
  // position. (Mobile post-pick vertical re-centering is handled in
  // useBracketScroll's scrollToMatch with no inline param.)
  //
  // The notification-arrival case ("you arrived from a notification, jump to
  // the most recent round with results") still updates displayRoundIndex via
  // jumpToLatestUpdatedRound — that just changes which round is rendered,
  // it no longer programmatically scrolls.

  return (
    <div style={{ width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', paddingLeft: '16px', paddingRight: '16px' }}>
      {/* Sticky round labels — sibling of the scroll container so sticky binds to page scroll */}
      <div
        className="sticky z-20 bg-background border-b border-border/40"
        style={{ top: 'var(--bracket-header-offset, 3rem)' }}
      >
        <div
          ref={labelsScrollRef}
          className="overflow-x-auto scrollbar-hide pl-1"
          style={{ touchAction: 'pan-x' }}
        >
          <div className="min-w-max">
            <div className="flex justify-center">
              <div className="flex" style={{ gap: '32px' }}>
                {bracketStructure.map((round) => (
                  <div key={round.roundNum} style={{ width: `${ROUND_WIDTH}px` }}>
                    <div className="flex items-center gap-2 py-2">
                      <h3 className="text-left font-bold text-sm text-muted-foreground">
                        {getRoundLabel(round.roundNum, round.matches.length)}
                      </h3>
                      <RoundLockBadge
                        lockInfo={getRoundLockInfo(round.roundNum)}
                        isEditingOfficial={isEditingOfficial}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={scroll.bracketScrollRef}
        data-bracket-scrollport
        className="scrollbar-visible"
        onPointerDown={scroll.handleBracketPointerDown}
        onPointerMove={scroll.handleBracketPointerMove}
        onPointerUp={scroll.handleBracketPointerUp}
        onPointerCancel={scroll.handleBracketPointerUp}
        style={{
          touchAction: 'auto',
          overflowX: 'scroll',
          overflowY: 'auto',
          ...(scroll.bracketHeight ? { maxHeight: scroll.bracketHeight } : {}),
        }}
      >
        <div
          ref={scroll.bracketInnerRef}
          className="relative pl-1 py-2 min-w-max"
        >
          <div className="relative" style={{ display: 'flex', justifyContent: 'center' }}>
            {/* Connector SVG must live INSIDE the round-columns flex (not as a
                sibling) so it shares the same horizontal origin. When the
                scroll container is wider than the bracket, the parent's
                justify-content: center shifts the columns right; an absolute
                SVG sibling stays at left:0 of the parent and falls behind by
                that shift, leaving a small gap on the left of every receiving
                card. Nesting the SVG inside the rounds-flex anchors it to
                the same coordinate origin as the round columns. */}
            <div className="flex relative z-[1]" style={{ gap: '32px' }}>
              <BracketConnectorLines />
              {bracketStructure.map((round) => {
                const { roundNum, matches: roundMatches } = round
                const { totalHeight } = getRoundLayout(roundNum, roundMatches)

                return (
                  <div key={roundNum} className={isCompact ? '' : 'relative'} style={{ width: `${ROUND_WIDTH}px`, minHeight: isCompact ? 'auto' : `${totalHeight}px` }}>
                    <div className={`${isCompact ? 'flex flex-col gap-6' : 'relative'}`} style={isCompact ? {} : { minHeight: `${totalHeight}px` }}>
                      {renderMatchCards(round)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
